import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'subsets', label: 'Subsets' },
  { key: 'permutations', label: 'Permutations' },
  { key: 'combsum', label: 'Combination Sum' },
  { key: 'nqueens', label: 'N-Queens' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const BoxRow = ({ label, values, getBoxState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minHeight: 46 }}>
      {values.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)', padding: '12px 0' }}>empty</span>}
      {values.map((v, i) => {
        const st = getBoxState ? getBoxState(i) : {};
        return (
          <div key={i} style={{
            width: 42, height: 42, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{v}</div>
        );
      })}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Subsets ─────────────────────────────────────────────────────── */
const SubsetsMode = () => {
  const [input, setInput] = useState('1, 2, 3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const candidates = input.split(',').map(s => s.trim()).filter(Boolean);
    if (candidates.length === 0 || candidates.length > 5) return;
    const built = [];
    const path = [];
    const results = [];
    const backtrack = start => {
      results.push([...path]);
      built.push({ path: [...path], results: results.length, action: 'record', note: `Path [${path.join(', ')}] is itself a valid subset — record it (subset #${results.length}).` });
      for (let i = start; i < candidates.length; i++) {
        path.push(candidates[i]);
        built.push({ path: [...path], results: results.length, action: 'choose', chosen: i, note: `Choose "${candidates[i]}" — add it to the path.` });
        backtrack(i + 1);
        path.pop();
        built.push({ path: [...path], results: results.length, action: 'unchoose', chosen: i, note: `Backtrack: remove "${candidates[i]}" from the path, try the next option.` });
      }
    };
    backtrack(0);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const candidates = input.split(',').map(s => s.trim()).filter(Boolean);
  const getCandState = i => (cur && cur.chosen === i && cur.action !== 'unchoose') ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};

  const code = {
    lines: [
      'void backtrack(int start) {',
      '    results.add(new ArrayList<>(path));   // every path IS a valid subset',
      '    for (int i = start; i < nums.length; i++) {',
      '        path.add(nums[i]);          // choose',
      '        backtrack(i + 1);            // explore',
      '        path.remove(path.size() - 1); // un-choose',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.action === 'record' ? 1 : cur.action === 'choose' ? 3 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="candidates" values={candidates} getBoxState={getCandState} />
        <BoxRow label="path (current partial subset)" values={cur ? cur.path : []} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1, 2, 3 (max 5)" />
          <button onClick={run} style={smallBtn(V1)}>Generate</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>{cur.note} <Chip color={GREEN}>{cur.results} subsets found so far</Chip></p>}
      </div>
      <CodePanel title="Java — Subsets (Choose / Explore / Unchoose)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Permutations ────────────────────────────────────────────────── */
const PermutationsMode = () => {
  const [input, setInput] = useState('1, 2, 3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const candidates = input.split(',').map(s => s.trim()).filter(Boolean);
    if (candidates.length === 0 || candidates.length > 4) return;
    const built = [];
    const path = [];
    const used = new Array(candidates.length).fill(false);
    const results = [];
    const backtrack = () => {
      if (path.length === candidates.length) {
        results.push([...path]);
        built.push({ path: [...path], used: [...used], results: results.length, action: 'record', note: `Path is full-length — record permutation #${results.length}.` });
        return;
      }
      for (let i = 0; i < candidates.length; i++) {
        if (used[i]) continue;
        used[i] = true; path.push(candidates[i]);
        built.push({ path: [...path], used: [...used], chosen: i, action: 'choose', note: `Choose "${candidates[i]}" (not yet used) — add it to the path.` });
        backtrack();
        used[i] = false; path.pop();
        built.push({ path: [...path], used: [...used], chosen: i, action: 'unchoose', note: `Backtrack: remove "${candidates[i]}", mark it unused again, try another option.` });
      }
    };
    backtrack();
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const candidates = input.split(',').map(s => s.trim()).filter(Boolean);
  const getCandState = i => {
    if (!cur) return {};
    if (cur.chosen === i && cur.action === 'choose') return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.used && cur.used[i]) return { bg: 'var(--bg2)', border: GREEN, color: 'var(--muted)' };
    return {};
  };

  const code = {
    lines: [
      'void backtrack() {',
      '    if (path.size() == nums.length) {',
      '        results.add(new ArrayList<>(path));',
      '        return;',
      '    }',
      '    for (int i = 0; i < nums.length; i++) {',
      '        if (used[i]) continue;',
      '        used[i] = true; path.add(nums[i]);   // choose',
      '        backtrack();                          // explore',
      '        used[i] = false; path.remove(path.size() - 1);  // un-choose',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.action === 'record' ? 2 : cur.action === 'choose' ? 7 : 9,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="candidates" values={candidates} getBoxState={getCandState} />
        <BoxRow label="path (current partial permutation)" values={cur ? cur.path : []} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1, 2, 3 (max 4)" />
          <button onClick={run} style={smallBtn(V1)}>Generate</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>{cur.note} <Chip color={GREEN}>{cur.results || 0} permutations found so far</Chip></p>}
      </div>
      <CodePanel title="Java — Permutations (Used[] Tracking)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Combination Sum ─────────────────────────────────────────────── */
const CombSumMode = () => {
  const [input, setInput] = useState('2, 3, 6, 7');
  const [targetInput, setTargetInput] = useState('7');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const candidates = input.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const target = Number(targetInput);
    if (candidates.length === 0 || isNaN(target)) return;
    const built = [];
    const path = [];
    let sum = 0;
    const results = [];
    const backtrack = start => {
      if (sum === target) {
        results.push([...path]);
        built.push({ path: [...path], sum, results: results.length, action: 'record', note: `Sum equals the target exactly — record combination #${results.length}.` });
        return;
      }
      for (let i = start; i < candidates.length; i++) {
        if (sum + candidates[i] > target) {
          built.push({ path: [...path], sum, action: 'prune', chosen: i, note: `Adding ${candidates[i]} would make the sum ${sum + candidates[i]} — over the target. Prune this branch immediately, don't even recurse.` });
          continue;
        }
        path.push(candidates[i]); sum += candidates[i];
        built.push({ path: [...path], sum, chosen: i, action: 'choose', note: `Choose ${candidates[i]}. Running sum: ${sum}.` });
        backtrack(i);   // reuse allowed — same index, not i + 1
        path.pop(); sum -= candidates[i];
        built.push({ path: [...path], sum, chosen: i, action: 'unchoose', note: `Backtrack: remove ${candidates[i]}. Running sum back to ${sum}.` });
      }
    };
    backtrack(0);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const candidates = input.split(',').map(s => s.trim()).filter(Boolean);
  const getCandState = i => {
    if (!cur || cur.chosen !== i) return {};
    if (cur.action === 'prune') return { bg: `linear-gradient(135deg,${RED},#c0293a)`, border: RED, color: '#fff' };
    if (cur.action === 'choose') return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'void backtrack(int start, int sum) {',
      '    if (sum == target) { results.add(new ArrayList<>(path)); return; }',
      '    for (int i = start; i < candidates.length; i++) {',
      '        if (sum + candidates[i] > target) continue;   // PRUNE',
      '        path.add(candidates[i]);',
      '        backtrack(i, sum + candidates[i]);   // i, not i+1 — reuse allowed',
      '        path.remove(path.size() - 1);',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.action === 'record' ? 1 : cur.action === 'prune' ? 3 : cur.action === 'choose' ? 4 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="candidates" values={candidates} getBoxState={getCandState} />
        <BoxRow label="path (current partial combination)" values={cur ? cur.path : []} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>target</span>
          <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 60 }} />
          <button onClick={run} style={smallBtn(V1)}>Generate</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        {cur && (
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
            {cur.note} <Chip color={TEAL}>sum = {cur.sum}</Chip>{cur.results !== undefined && <Chip color={GREEN}>{cur.results} found</Chip>}
          </p>
        )}
      </div>
      <CodePanel title="Java — Combination Sum (With Pruning)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── N-Queens ────────────────────────────────────────────────────── */
const NQueensMode = () => {
  const [n, setN] = useState(4);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = size => {
    const built = [];
    const queens = new Array(size).fill(-1);
    let solutions = 0;
    const isSafe = (row, col) => {
      for (let r = 0; r < row; r++) {
        if (queens[r] === col || Math.abs(queens[r] - col) === row - r) return false;
      }
      return true;
    };
    const backtrack = row => {
      if (row === size) {
        solutions++;
        built.push({ queens: [...queens], row, action: 'solution', solutions, note: `All ${size} rows filled with no conflicts — solution #${solutions}!` });
        return;
      }
      for (let col = 0; col < size; col++) {
        if (!isSafe(row, col)) {
          built.push({ queens: [...queens], row, col, action: 'conflict', note: `Column ${col} conflicts with an existing queen (same column or diagonal) — skip.` });
          continue;
        }
        queens[row] = col;
        built.push({ queens: [...queens], row, col, action: 'place', note: `Row ${row}, column ${col} is safe — place a queen there.` });
        backtrack(row + 1);
        queens[row] = -1;
        built.push({ queens: [...queens], row, col, action: 'remove', note: `Backtrack: remove the queen from row ${row}, try the next column.` });
      }
    };
    backtrack(0);
    built.push({ done: true, solutions });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'void backtrack(int row) {',
      '    if (row == n) { solutions++; return; }',
      '    for (int col = 0; col < n; col++) {',
      '        if (!isSafe(row, col)) continue;   // conflict — skip',
      '        queens[row] = col;',
      '        backtrack(row + 1);',
      '        queens[row] = -1;    // un-place on backtrack',
      '    }',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.action === 'solution' ? 1 : cur.action === 'conflict' ? 3 : cur.action === 'place' ? 4 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
          {Array.from({ length: n }, (_, r) => (
            <div key={r} style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: n }, (_, c) => {
                const q = cur ? cur.queens : new Array(n).fill(-1);
                const hasQueen = q && q[r] === c;
                const isCurrentAttempt = cur && !cur.done && cur.row === r && cur.col === c;
                let bg = (r + c) % 2 === 0 ? 'var(--bg3)' : 'var(--bg2)';
                let border = 'var(--border)', color = 'var(--text)';
                if (hasQueen) { bg = `linear-gradient(135deg,${V1},${V2})`; border = V1; color = '#fff'; }
                else if (isCurrentAttempt && cur.action === 'conflict') { bg = `linear-gradient(135deg,${RED},#c0293a)`; border = RED; color = '#fff'; }
                return (
                  <div key={c} style={{
                    width: 36, height: 36, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: bg, border: `1.5px solid ${border}`, color, fontWeight: 800, fontSize: 15, transition: 'all 0.2s ease',
                  }}>{hasQueen ? '♛' : ''}</div>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>N</span>
          <input value={n} onChange={e => setN(Math.min(8, Math.max(4, parseInt(e.target.value, 10) || 4)))} style={{ ...smallInput, width: 60 }} type="number" min={4} max={8} />
          <button onClick={() => run(n)} style={smallBtn(V1)}>Solve</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Choose a board size (4–8), then Solve — watch queens get placed, conflict-checked, and backtracked.` :
            cur.done ? `Search complete: ${cur.solutions} total solution${cur.solutions === 1 ? '' : 's'} found for N=${n}.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — N-Queens Backtracking" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const candidates = [2, 3, 5, 6];
  const target = 8;
  const pathLen = 1 + Math.floor(Math.random() * 2);
  const path = candidates.slice(0, pathLen);
  const sum = path.reduce((a, b) => a + b, 0);
  const nextIdx = pathLen + Math.floor(Math.random() * (candidates.length - pathLen));
  const next = candidates[nextIdx];
  const wouldBe = sum + next;
  const correct = wouldBe <= target ? 'continue' : 'prune';
  return { path, sum, next, target, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'continue', label: `Choose it — sum stays ≤ target (${scenario.sum} + ${scenario.next} = ${scenario.sum + scenario.next} ≤ ${scenario.target})` },
    { key: 'prune', label: `Prune — sum would exceed the target` },
  ];

  return (
    <div>
      <BoxRow label="path so far" values={scenario.path} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '14px 0' }}>
        target = {scenario.target}, current sum = {scenario.sum}. The next candidate under consideration is {scenario.next}. What should the backtracking do?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const BacktrackingPlayground = () => {
  const [mode, setMode] = useState('subsets');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'subsets' && <SubsetsMode />}
      {mode === 'permutations' && <PermutationsMode />}
      {mode === 'combsum' && <CombSumMode />}
      {mode === 'nqueens' && <NQueensMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default BacktrackingPlayground;
